import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useLocation, useNavigate } from "react-router-dom";
import HyperpayCheckoutInfo, {
  HyperpayFinalizedCheckoutInfoType,
} from "../../../components/payment/HyperpayCheckoutInfo";
import { AuthContextInterface, useAuth } from "../../../context/AuthContext";
import {
  HyperpayCheckoutRequestBody,
  createHyperpayCheckoutSession,
  savePaymentAddress,
} from "../../../api/endpoints/hyperpay/hayperpayEndpoints";
import toast from "react-hot-toast";
import { countryCodeLookupByName } from "../../../helpers/options/countries";
import PaymentModel from "../../../api/models/PaymentModel";
import { useEffect, useState } from "react";
import { FullSpinner } from "../../../components/LoadingSpinner";
import HyperpayPaymentForm from "../../../components/payment/HyperpayPaymentForm";
import { IndexProgramSubscriptions } from "../../../api/endpoints/subscriptions/subscriptionEndpoints";
import SubscriptionsModel from "../../../api/models/SubscriptionsModel";
import NoMorePaymentDisclaimer from "../../../components/payment/NoMorePaymentDisclaimer";
import OfflinePaymentDisclaimer from "../../../components/payment/OfflinePaymentDisclaimer";
type PaymentStudentPageProps = {
  redirectUrl?: string;
};

const DEFAULT_MAX_PAYMENT = 550;
const DEFAULT_MIN_PAYMENT = 1;
export default function PaymentStudentPage(props: PaymentStudentPageProps) {
  const { t } = useTranslation("", {
    keyPrefix: "page.candidate.dashboard.payment",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser }: AuthContextInterface = useAuth()!;
  const [paymentIntentId, setPaymentIntentId] = useState<undefined | string>();
  const [maxPaymentAmount, setMaxPaymentAmount] =
    useState<number>(DEFAULT_MAX_PAYMENT);
  const [minPaymentAmount, setMinPaymentAmount] =
    useState<number>(DEFAULT_MIN_PAYMENT);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setMinAndMaxPayments();
  }, []);

  const onConfirmCheckout = async (
    info: HyperpayFinalizedCheckoutInfoType
  ): Promise<void> => {
    setIsLoading(true);
    const body: HyperpayCheckoutRequestBody = {
      ...info,
      currency: "USD",
      amount: info.amount * 100,
      country: countryCodeLookupByName(info.country)!,
    };
    const response = await createHyperpayCheckoutSession(body);
    savePaymentAddress(body);
    if (response.isSuccess) {
      const payment: PaymentModel = PaymentModel.fromApiResponse(response.data);
      setPaymentIntentId(payment.paymentIntentId);
    } else {
      console.error(response.errorMessages);
      toast.error("Unable to start payment at the moment");
    }
    setIsLoading(false);
  };

  const setMinAndMaxPayments = async () => {
    const response = await IndexProgramSubscriptions();
    if (response.isSuccess) {
      const subscriptions = SubscriptionsModel.fromApiResponse(
        response.data || []
      ) as SubscriptionsModel[];
      const subscription = subscriptions.at(-1);
      if (!!subscription) {
        setMinPaymentAmount(subscription.minPayment);
        setMaxPaymentAmount(subscription.maxPayment);
      }
    }
  };

  if (!currentUser || isLoading) return <FullSpinner size={50} />;
  if (minPaymentAmount <= 0) return <NoMorePaymentDisclaimer />;

  return (
    <div className="w-full overflow-x-hidden bg-gray-50">
      <div className="w-full border-b border-gray-200 py-6 px-4 md:px-6">
        <div className="w-full flex justify-between  items-center">
          <h3 className="w-full mb-1 text-lg leading-6 text-gray-900 font-medium">
            {t("paymentSubscriptions")}
          </h3>
          {location.pathname !== "/student/dashboard/payment" && (
            <ArrowLeftIcon
              className="w-6 h-6 cursor-pointer ltr:rotate-180"
              onClick={() => navigate("/")}
            />
          )}
        </div>
        <p className="w-full text-gray-500 text-sm font-normal">
          {t("paymentDetails")}
        </p>
      </div>
      <div className="w-full flex justify-center items-center mb-12 px-4 md:px-6">
        {!paymentIntentId ? (
          <HyperpayCheckoutInfo
            minPaymentAmount={minPaymentAmount}
            maxPaymentAmount={maxPaymentAmount}
            initialAddress={currentUser!.paymentAddress}
            onConfirm={onConfirmCheckout}
          />
        ) : (
          <HyperpayPaymentForm
            paymentId={paymentIntentId}
            redirectTo={`${window.location.protocol}//${
              window.location.hostname
            }${
              !!window.location.port ? `:${window.location.port}` : ""
            }/payment/verify/${paymentIntentId}`}
          />
        )}
      </div>
    </div>
  );
}
